import { ActualityHqWithCongestionsRow } from '@warebee/shared/dataset-queries';
import { TFunction } from 'i18next';
import {
  EVENT_GROUPING_CONFIG,
  EventGrouping,
  getIssuesForGrouping
} from '../constants/actuality.event.config';

/**
 * Get the most important stats for a given grouping
 */
export const getPrimaryStatsForGrouping = (grouping: EventGrouping) => {
  const config = EVENT_GROUPING_CONFIG[grouping];
  return config.primaryStats;
};

/**
 * Get the secondary stats for a given grouping
 */
export const getSecondaryStatsForGrouping = (grouping: EventGrouping) => {
  const config = EVENT_GROUPING_CONFIG[grouping];
  return config.secondaryStats;
};

/**
 * Get the default issues to show for a given grouping
 */
export const getDefaultIssuesForGrouping = (grouping: EventGrouping) => {
  const config = EVENT_GROUPING_CONFIG[grouping];
  return config.defaultIssues;
};

/**
 * Utility to get a summary of an event based on grouping
 */
export const getEventSummary = (
  event: ActualityHqWithCongestionsRow, 
  grouping: EventGrouping,
  t: TFunction
): {
  title: string;
  subtitle: string;
  primaryInfo: string[];
  issueCount: number;
} => {
  const config = EVENT_GROUPING_CONFIG[grouping];
  const issues = getIssuesForGrouping(grouping);
  const activeIssues = issues.filter(issue => issue.condition(event));

  // Generate title based on grouping
  let title = '';
  let subtitle = '';
  let primaryInfo: string[] = [];

  switch (grouping) {
    // case 'agentId':
    //   title = event.agentId;
    //   subtitle = event.agentType;
    //   primaryInfo = [event.locationId, event.sku];
    //   break;
    case 'agent':
      title = event.agentType;
      subtitle = event.eventProcessType;
      primaryInfo = [event.agentId, event.locationId];
      break;
    case 'location':
      title = event.locationId;
      subtitle = event.layoutWarehouseArea || '';
      primaryInfo = [event.agentId, event.sku];
      break;
    case 'item':
      title = event.sku;
      subtitle = event.consignee;
      primaryInfo = [event.locationId, `${event.quantity} ${event.uom}`];
      break;
    case 'process':
      title = event.eventProcessType;
      subtitle = event.eventType || '';
      primaryInfo = [event.agentId, event.locationId];
      break;
    case 'time':
      title = event.eventEndTime.toLocaleTimeString();
      subtitle = event.eventEndTime.toLocaleDateString();
      primaryInfo = [event.agentId, event.eventProcessType];
      break;
    default:
      title = event.eventId;
      subtitle = event.eventProcessType;
      primaryInfo = [event.agentId, event.locationId, event.sku];
  }

  return {
    title,
    subtitle,
    primaryInfo,
    issueCount: activeIssues.length,
  };
};

/**
 * Get recommended layout based on available space and grouping
 */
export const getRecommendedLayout = (
  grouping: EventGrouping,
  availableSpace: 'compact' | 'normal' | 'expanded'
): 'mini' | 'list' | 'grid' => {
  const primaryStatsCount = getPrimaryStatsForGrouping(grouping).length;
  
  if (availableSpace === 'compact') {
    return 'mini';
  }
  
  if (availableSpace === 'normal') {
    return primaryStatsCount > 6 ? 'mini' : 'list';
  }
  
  // Expanded space
  return primaryStatsCount > 10 ? 'grid' : 'list';
};

/**
 * Get configuration for different display contexts
 */
export const getConfigForContext = (context: 'list' | 'detail' | 'card' | 'popup') => {
  switch (context) {
    case 'list':
      return {
        onlyVisible: true,
        layout: 'mini' as const,
        showCategoryTitles: false,
        separateByTiming: false,
      };
    case 'detail':
      return {
        onlyVisible: false,
        layout: 'list' as const,
        showCategoryTitles: true,
        separateByTiming: true,
      };
    case 'card':
      return {
        onlyVisible: true,
        layout: 'list' as const,
        showCategoryTitles: false,
        separateByTiming: false,
      };
    case 'popup':
      return {
        onlyVisible: false,
        layout: 'grid' as const,
        showCategoryTitles: true,
        separateByTiming: true,
      };
    default:
      return {
        onlyVisible: true,
        layout: 'list' as const,
        showCategoryTitles: false,
        separateByTiming: false,
      };
  }
};

/**
 * Get stats that are most relevant for comparison between events
 */
export const getComparisonStats = (grouping: EventGrouping): string[] => {
  const config = EVENT_GROUPING_CONFIG[grouping];
  
  // Return first 3-4 primary stats that are good for comparison
  return config.primaryStats.slice(0, 4);
};

/**
 * Get color coding based on issue severity for an event
 */
export const getEventStatusColor = (
  event: ActualityHqWithCongestionsRow,
  grouping: EventGrouping
): 'success' | 'warning' | 'error' | 'info' => {
  const issues = getIssuesForGrouping(grouping);
  const activeIssues = issues.filter(issue => issue.condition(event));
  
  if (activeIssues.some(issue => issue.severity === 'error')) {
    return 'error';
  }
  
  if (activeIssues.some(issue => issue.severity === 'warning')) {
    return 'warning';
  }
  
  if (activeIssues.length > 0) {
    return 'info';
  }
  
  return 'success';
}; 